import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Link,
  Skeleton,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { CreditAgreementValidationErrorGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useGetCreditAgreementValidationErrorsQuery } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/required-tasks/requiredTasks.gql.generated';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';

const TASK_TYPE_TO_PATH = {
  party: 'parties',
  facility: 'facilityMechanics',
  pricingOption: 'pricing-options',
  commitment: 'commitments',
  expense: 'expenseMechanics',
};
const TASK_TYPE_TO_TITLE = {
  party: 'Parties',
  facility: 'Facilities',
  pricingOption: 'Pricing Options',
  commitment: 'Commitments',
  expense: 'Expenses & Fees',
};

const RequiredTasksSectionByType = ({
  requiredTasks,
  type,
}: {
  requiredTasks: CreditAgreementValidationErrorGQLType[];
  type: keyof typeof TASK_TYPE_TO_PATH;
}) => {
  if (!requiredTasks.some((requiredTask) => requiredTask.type === type))
    return null;

  return (
    <VStack w="100%" alignItems="flex-start">
      <Typography variant="section-subtitle" trim>
        {TASK_TYPE_TO_TITLE[type]}
      </Typography>
      {requiredTasks
        .filter((requiredTask) => requiredTask.type === type)
        .map((requiredTask) => (
          <Alert key={requiredTask.message} size="lg" status="error">
            <AlertIcon />
            <Flex direction="column" flexGrow={1}>
              <AlertTitle>{requiredTask.message}</AlertTitle>
            </Flex>
            <Link as={RouterLink} to={`./${TASK_TYPE_TO_PATH[type]}`}>
              <Typography
                colorScheme="blue"
                fontWeight="medium"
                variant="base-muted"
              >
                Link
              </Typography>
            </Link>
          </Alert>
        ))}
    </VStack>
  );
};

const RequiredTasksDrawer = ({
  drawerControls,
  requiredTasks,
}: {
  drawerControls: ReturnType<typeof useDisclosure>;
  requiredTasks: CreditAgreementValidationErrorGQLType[];
}) => {
  return (
    <Drawer {...drawerControls} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader textAlign="left" mt="1.5rem" px="3rem" pb="0">
          Required Tasks for Submission
        </DrawerHeader>
        <Divider my="1.5rem" />
        <DrawerBody as={VStack} spacing="2rem" px="3rem">
          <Typography variant="base-muted">
            All required actions must be completed to submit for approval.
            Incomplete actions will prevent submission.
          </Typography>
          {requiredTasks.length === 0 ? (
            <Alert size="lg" status="success">
              <AlertIcon />
              <AlertTitle>
                All required actions have been completed. Ready to submit for
                approval.
              </AlertTitle>
            </Alert>
          ) : (
            <>
              <RequiredTasksSectionByType
                requiredTasks={requiredTasks}
                type="party"
              />
              <RequiredTasksSectionByType
                requiredTasks={requiredTasks}
                type="facility"
              />
              <RequiredTasksSectionByType
                requiredTasks={requiredTasks}
                type="pricingOption"
              />
              <RequiredTasksSectionByType
                requiredTasks={requiredTasks}
                type="commitment"
              />
              <RequiredTasksSectionByType
                requiredTasks={requiredTasks}
                type="expense"
              />
            </>
          )}
          <VStack align="flex-start" />
        </DrawerBody>
        <Divider />
      </DrawerContent>
      <DrawerFooter flex="end">
        <Button variant="primary">Review Changes</Button>
      </DrawerFooter>
    </Drawer>
  );
};

export const RequiredTasksAlert = () => {
  const drawerControls = useDisclosure();
  const flags = useFeatureFlags();

  const latestDraftCreditAgreementId = useCreditAgreementStore(
    (state) => state.creditAgreement?.id,
  );

  // todo @mark linear.app/finleycms/issue/ENG-16681/successful-wip-save-should-reload-get-required-tasks
  const { data, error, loading } = useGetCreditAgreementValidationErrorsQuery({
    variables: {
      creditAgreementId: latestDraftCreditAgreementId ?? '',
    },
    skip: !latestDraftCreditAgreementId,
    fetchPolicy: 'no-cache',
  });

  const requiredTasks = useMemo(
    () => data?.getCreditAgreementValidationErrors ?? [],
    [data],
  );

  if (flags.productsImplementationPortalPermissions !== 'allow') return null;
  if (error) throw error;

  const isRequiredTasksLoading =
    loading || !data?.getCreditAgreementValidationErrors;

  return (
    <>
      {isRequiredTasksLoading ? (
        <Skeleton h="2.5rem" />
      ) : (
        <>
          <Alert
            size="lg"
            status={requiredTasks.length === 0 ? 'success' : 'info'}
          >
            <AlertIcon />
            <Flex direction="column" flexGrow={1}>
              <AlertDescription>
                {requiredTasks.length === 0
                  ? 'All required actions have been completed. Ready to submit for approval.'
                  : 'All required actions must be completed to submit for approval.'}
              </AlertDescription>
            </Flex>
            <Button
              variant="link"
              colorScheme="blue"
              aria-label="View actions"
              onClick={drawerControls.onOpen}
            >
              View Actions
            </Button>
          </Alert>
          <RequiredTasksDrawer
            drawerControls={drawerControls}
            requiredTasks={requiredTasks}
          />
        </>
      )}
    </>
  );
};
